import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useCart } from '../store/cartStore';
import './CartPage.css';

const getCurrencySymbol = (code) => {
  if (!code) return '';
  const normalized = code.trim().toUpperCase();
  switch (normalized) {
    case 'USD':
      return '$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    case 'JPY':
      return '¥';
    case 'CAD':
      return 'C$';
    case 'AUD':
      return 'A$';
    default:
      return normalized;
  }
};

const formatEventDateTime = (dateString) => {
  if (!dateString) return 'Date to be confirmed';
  return new Date(dateString).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const CartPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuthStore();
  const {
    cartItems,
    removeFromCart,
    updateQuantity,

    getCartCount,
    validateCartContents,
    error: cartError
  } = useCart();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [validating, setValidating] = useState(false);
  const [notification, setNotification] = useState(null);
  const [now, setNow] = useState(Date.now());
  const lastExpirySyncRef = React.useRef(false);
  const syncingRef = React.useRef(false);

  useEffect(() => setError(null), []);

  useEffect(() => {
    if (location.state?.checkoutAbandoned) {
      setNotification('Checkout cancelled. Your reserved tickets have been released.');
      navigate(location.pathname, { replace: true, state: {} });
      setTimeout(() => setNotification(null), 6000);
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => setError(null), [cartItems]);

  useEffect(() => {
    const initializeCart = async () => {
      try {
        setLoading(true);
        const prevItems = useCart.getState().cartItems || [];
        await useCart.getState().loadCartFromBackend();
        const nextItems = useCart.getState().cartItems || [];

        const prevByType = new Set(prevItems.map((i) => i.ticketTypeId));
        const nextByType = new Set(nextItems.map((i) => i.ticketTypeId));
        const removedCount = [...prevByType].filter((id) => !nextByType.has(id)).length;
        if (removedCount > 0) {
          setNotification(
            removedCount === 1
              ? 'One reservation expired and was removed from your cart.'
              : `${removedCount} reservations expired and were removed from your cart.`
          );
          setTimeout(() => setNotification(null), 7000);
        }
      } catch (err) {
        setError('Failed to load cart. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) initializeCart();
  }, [user]);

  useEffect(() => {
    if (cartError) {
      if (removing || validating) {
        setError(cartError);
      }
    }
  }, [cartError, removing, validating]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRemove = async (item) => {
    try {
      // Use component-level removing state or local if cleaner
      setRemoving(item.id);
      if (item.seatId) {
        await useCart.getState().removeSpecificSeat(item.eventId, item.seatId);
      } else {
        await removeFromCart(item.ticketTypeId);
      }
    } catch (err) {
      setError('Failed to remove item.');
    } finally {
      setRemoving(null);
    }
  };

  const handleUpdateQuantity = async (ticketTypeId, quantity) => {
    try {
      await updateQuantity(ticketTypeId, quantity);
    } catch (err) {
      setError('Failed to update quantity.');
    }
  };

  const createOrderAndGetData = async () => {
    if (!user?.id) return null;

    try {
      setValidating(true);
      setError(null);

      if (!cartItems.length) {
        setError('Your cart is empty.');
        return null;
      }

      const validation = await validateCartContents();
      if (!validation.isValid) {
        const errorMessage = validation.errors?.join(', ') || 'Cart validation failed';
        setError(`Cart validation failed: ${errorMessage}`);
        return null;
      }

      const cartPayload = cartItems.map((item) => ({
        ticketTypeId: item.ticketTypeId,
        quantity: item.quantity
      }));

      const currencyToUse = (cartItems[0]?.currencyCode || 'EUR').toUpperCase();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/checkout/start`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`
          },
          body: JSON.stringify({
            userId: user.id,
            cartItems: cartPayload,
            currencyCode: currencyToUse
          })
        }
      );

      if (!response.ok) {
        let message = 'Failed to start checkout';
        let errorCode = null;
        try {
          const errorBody = await response.json();
          errorCode = errorBody?.error?.code || null;
          message =
            errorBody?.message ||
            errorBody?.data?.message ||
            errorBody?.error?.message ||
            message;

          if (['CART_CHANGED', 'CART_EXPIRED', 'HOLDS_EXPIRED'].includes(errorCode)) {
            await useCart.getState().loadCartFromBackend();
            setNotification('One or more reservations expired. Your cart has been updated — please review and try again.');
            setTimeout(() => setNotification(null), 7000);
            setError(message);
            return null;
          }
        } catch {
          // ignore non-JSON responses
        }
        throw new Error(message);
      }

      const apiResponse = await response.json();
      if (!apiResponse.success || !apiResponse.data?.success || !apiResponse.data?.order) {
        setError(apiResponse.message || apiResponse.data?.message || 'Failed to start checkout');
        return null;
      }

      const checkoutResult = apiResponse.data;

      // Prioritize redirect URL (Stripe Checkout)
      if (checkoutResult.redirectUrl) {
        window.location.href = checkoutResult.redirectUrl;
        return true;
      }

      // Fallback: navigation/embedded logic
      navigate('/checkout', {
        state: {
          orderId: checkoutResult.order.id,
          clientSecret: checkoutResult.paymentIntentClientSecret
        }
      });
      return true;

    } catch (err) {
      setError('Failed to prepare checkout: ' + (err.message || 'Unknown error'));
      return null;
    } finally {
      setValidating(false);
    }
  };

  const { subtotal, fees, taxes, total } = useMemo(() => {
    let subtotal = 0;
    let calculatedFees = 0;
    let calculatedTaxes = 0;

    cartItems.forEach((item) => {
      const quantity = Number(item.quantity || 0);
      const unitPrice = Number(item.unitPrice || 0);
      const itemSubtotal = unitPrice * quantity;

      subtotal += itemSubtotal;

      // Service Fee is fixed amount per ticket
      if (item.serviceFee) {
        calculatedFees += Number(item.serviceFee) * quantity;
      }

      // Tax is percentage of item subtotal
      if (item.tax) {
        calculatedTaxes += itemSubtotal * (Number(item.tax) / 100);
      }
    });

    return {
      subtotal,
      fees: calculatedFees,
      taxes: calculatedTaxes,
      total: subtotal + calculatedFees + calculatedTaxes
    };
  }, [cartItems]);

  const soonestExpiry = useMemo(() => {
    const times = cartItems.map((item) => item.expiresAt && new Date(item.expiresAt).getTime()).filter(Boolean);
    if (!times.length) return null;
    return new Date(Math.min(...times));
  }, [cartItems]);

  useEffect(() => {
    const syncIfExpired = async () => {
      if (!soonestExpiry) return;
      const diffMs = soonestExpiry.getTime() - now;
      if (diffMs > 0) {
        lastExpirySyncRef.current = false;
        return;
      }
      if (lastExpirySyncRef.current || syncingRef.current) return;
      lastExpirySyncRef.current = true;
      syncingRef.current = true;
      try {
        const prevItems = useCart.getState().cartItems || [];
        await useCart.getState().loadCartFromBackend();
        const nextItems = useCart.getState().cartItems || [];

        const prevByType = new Set(prevItems.map((i) => i.ticketTypeId));
        const nextByType = new Set(nextItems.map((i) => i.ticketTypeId));
        const removedCount = [...prevByType].filter((id) => !nextByType.has(id)).length;
        if (removedCount > 0) {
          setNotification(
            removedCount === 1
              ? 'One reservation expired and was removed from your cart.'
              : `${removedCount} reservations expired and were removed from your cart.`
          );
          setTimeout(() => setNotification(null), 7000);
        }
      } finally {
        syncingRef.current = false;
      }
    };
    syncIfExpired();
  }, [soonestExpiry, now]);

  const expiryLabel = useMemo(() => {
    if (!soonestExpiry) return null;
    const diffMs = soonestExpiry.getTime() - now;
    if (diffMs <= 0) return 'Some reservations have expired';
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin <= 1) return 'Reservations expire in ~1 minute';
    if (diffMin < 60) return `Reservations expire in ~${diffMin} minutes`;
    const hours = Math.floor(diffMin / 60);
    const minutes = diffMin % 60;
    return `Reservations expire in ~${hours}h ${minutes}m`;
  }, [soonestExpiry, now]);

  const hasItems = cartItems.length > 0;
  const cartCount = getCartCount();
  const ticketLabel = cartCount === 1 ? 'ticket' : 'tickets';

  const currencyCode = cartItems[0]?.currencyCode || 'EUR';
  const currencySymbol = getCurrencySymbol(currencyCode);

  if (loading) {
    return (
      <div className="cart-shell">
        <div className="cart-container">
          <header className="cart-header">
            <h1>Cart</h1>
            <div className="cart-subtitle">Loading…</div>
          </header>
          <div className="cart-loading">Loading your cart…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-shell">
      <div className="cart-container">
        <header className="cart-header">
          <h1>Cart</h1>
          <div className="cart-subtitle">{hasItems ? `${cartCount} ${ticketLabel}` : 'No tickets yet'}</div>
        </header>

        {notification && (
          <div className="cart-banner">
            <div>
              <strong>Heads up:</strong> {notification}
            </div>
            <button type="button" onClick={() => setNotification(null)} aria-label="Dismiss">
              ×
            </button>
          </div>
        )}

        {error && <div className="cart-error-banner">{error}</div>}

        {!hasItems ? (
          <div className="cart-panel cart-empty">
            <h2>Your cart is empty</h2>
            <p>Browse events and reserve tickets — we’ll keep them safe while you check out.</p>
            <button className="cart-browse-btn" onClick={() => navigate('/events')}>
              Browse events
            </button>
          </div>
        ) : (
          <div className="cart-grid">
            <div className="cart-panel">
              <div className="cart-panel-header">
                <div className="cart-panel-title">Reserved items</div>
                <div className="cart-panel-hint">{expiryLabel || 'Tickets are reserved temporarily.'}</div>
              </div>

              <div className="cart-items">
                {cartItems.map((item) => {
                  const unitPrice = Number(item.unitPrice || 0);
                  const totalPrice = Number(item.totalPrice || unitPrice * item.quantity || 0);
                  const venueLabel = item.venueName || 'Venue to be announced';
                  const formattedDate = formatEventDateTime(item.eventDate);
                  const key = item.id || item.ticketTypeId;

                  const candidateTags = [
                    item.ticketTypeName ? item.ticketTypeName : null
                  ].filter(Boolean);

                  const tags = (() => {
                    const seen = new Set();
                    const unique = [];
                    for (const tag of candidateTags) {
                      const key = String(tag).trim().toLowerCase();
                      if (seen.has(key)) continue;
                      seen.add(key);
                      unique.push(tag);
                    }
                    return unique;
                  })();

                  return (
                    <div className="cart-item" key={key}>
                      <div className="cart-item-details">
                        <div
                          className="cart-item-title"
                          onClick={() => navigate(`/events/${item.eventId}`)}
                          style={{ cursor: 'pointer' }}
                          title="View Event Details"
                        >
                          {item.eventTitle || 'Event'}
                        </div>
                        <div className="cart-item-meta">
                          {formattedDate} • {venueLabel}
                        </div>
                        <div className="cart-tags">
                          {item.seatLabels && item.seatLabels.length > 0 && (
                            <span className="cart-seat-badge" style={{ marginLeft: 0 }}>
                              Seat: {item.seatLabels.join(', ')}
                            </span>
                          )}
                          {tags.slice(0, 3).map((tag) => (
                            <span className="cart-tag" key={tag}>{tag}</span>
                          ))}
                          {tags.length > 3 && <span className="cart-tag">+{tags.length - 3} more</span>}
                        </div>
                      </div>

                      <div className="cart-item-actions">
                        <div className="cart-price-group">
                          <div className="cart-total-price">
                            {currencySymbol}{totalPrice.toFixed(2)}
                          </div>
                          <div className="cart-unit-price">
                            {currencySymbol}{unitPrice.toFixed(2)} ea
                          </div>
                        </div>

                        <div className="cart-controls">
                          {item.seatAssignment !== 'ASSIGNED' ? (
                            <div className="cart-stepper">
                              <button
                                type="button"
                                onClick={() => handleUpdateQuantity(item.ticketTypeId, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                                aria-label="Decrease quantity"
                              >
                                –
                              </button>
                              <span>{item.quantity}</span>
                              <button
                                type="button"
                                onClick={() => handleUpdateQuantity(item.ticketTypeId, item.quantity + 1)}
                                aria-label="Increase quantity"
                              >
                                +
                              </button>
                            </div>
                          ) : null}

                          <button
                            className="cart-remove-btn"
                            type="button"
                            onClick={() => handleRemove(item)}
                            aria-label="Remove item"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="cart-panel cart-summary">
              <div className="cart-panel-header">
                <div className="cart-panel-title">Summary</div>
              </div>
              <div className="cart-summary-body">
                <div className="cart-summary-row">
                  <span>Subtotal</span>
                  <span className="value">{currencySymbol}{subtotal.toFixed(2)}</span>
                </div>
                {fees > 0 && (
                  <div className="cart-summary-row">
                    <span>Service Fees</span>
                    <span className="value">{currencySymbol}{fees.toFixed(2)}</span>
                  </div>
                )}
                {taxes > 0 && (
                  <div className="cart-summary-row">
                    <span>Taxes</span>
                    <span className="value">{currencySymbol}{taxes.toFixed(2)}</span>
                  </div>
                )}
                <div className="cart-summary-total">
                  <span className="label">Total</span>
                  <span className="value">{currencySymbol}{total.toFixed(2)}</span>
                </div>
                <button
                  className="cart-checkout-btn"
                  onClick={createOrderAndGetData}
                  disabled={validating}
                >
                  {validating ? 'Processing...' : 'Checkout'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
